import React, { useEffect, useState, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import "../styles/Navbar.css";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";

const NAV_LINKS = [
  { to: "/", label: "Home", end: true },
  { to: "/experience", label: "Experience" },
  { to: "/projects", label: "Projects" },
];

// HealthSearch is the featured project, so its tab is drawn as a glowing pill with a live dot
// instead of a plain text link.
const FEATURED = { to: "/healthsearch", label: "HealthSearch", blurb: "Health answers, hospitals and prices" };

// A green dot that pulses, saying the app is live.
const liveDot = (
  <span aria-hidden="true" className="relative flex h-2.5 w-2.5 shrink-0">
    <span className="live-ping absolute inline-flex h-full w-full rounded-full bg-[#5ef0a8] opacity-75" />
    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#38e08f] ring-2 ring-white/70" />
  </span>
);

const featuredDesktopClass = ({ isActive }) =>
  "featured-tab relative flex items-center gap-2 whitespace-nowrap rounded-full px-4 xl:px-5 py-2 xl:py-2.5 text-[16px] xl:text-[18px] font-bold text-white transition " +
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-white " +
  (isActive ? "is-active" : "");

// Header text uses solid colors and the semibold face: see-through white and Segoe UI's
// regular face (what weight 500 falls back to on Windows) render thin and jagged on the bar.
const softText = "text-[#fde3e0] dark:text-[#cfd3df]";

// Desktop link: soft white text, with an underline marking the page you're on.
const desktopLinkClass = ({ isActive }) =>
  "nav-link relative whitespace-nowrap px-1 py-2 text-[17px] xl:text-[20px] font-semibold transition-colors " +
  (isActive ? "text-white nav-link-active" : `${softText} hover:text-white`);

// Mobile link: a full-width row, with a tinted pill marking the page you're on.
const mobileLinkClass = ({ isActive }) =>
  "flex items-center rounded-xl px-4 py-3 text-[19px] font-semibold transition-colors " +
  (isActive ? "bg-white/15 text-white" : `${softText} hover:bg-white/10 hover:text-white`);

function Navbar(props) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showSunshine, setShowSunshine] = useState(false);
  const [showClouds, setShowClouds] = useState(false);
  const [animVersion, setAnimVersion] = useState(0);
  // The theme the animations last played for. Comparing against it, rather than skipping the
  // first run, keeps StrictMode's second mount-time run from playing the sun burst on page load.
  const shownTheme = useRef(props.darkMode);
  const menuButtonRef = useRef(null);

  const location = useLocation();

  // Close the menu after navigating.
  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  // While the menu is open: Escape closes it, and the page behind it doesn't scroll.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  // If the window grows wide enough for the full row of links, drop the open menu.
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = (e) => e.matches && setMenuOpen(false);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  useEffect(() => {
    if (shownTheme.current === props.darkMode) return;
    shownTheme.current = props.darkMode;

    setAnimVersion((prev) => prev + 1);

    if (!props.darkMode) {
      setShowClouds(false);
      setShowSunshine(true);
      const timer = setTimeout(() => setShowSunshine(false), 1200);
      return () => clearTimeout(timer);
    } else {
      setShowSunshine(false);
      setShowClouds(true);
      const timer = setTimeout(() => setShowClouds(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [props.darkMode]);

  const themeToggle = (
    <button
      type="button"
      onClick={props.toggleTheme}
      aria-label={props.darkMode ? "Switch to light mode" : "Switch to dark mode"}
      title={props.darkMode ? "Switch to light mode" : "Switch to dark mode"}
      className="theme-toggle relative flex h-[52px] w-[52px] xl:h-14 xl:w-14 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
    >
      {showSunshine && (
        <span key={animVersion} className="sunshine-container" aria-hidden="true">
          {[...Array(12)].map((_, i) => (
            <span key={i} className="ray-wrapper" style={{ transform: `rotate(${i * 30}deg)` }}>
              <span className="ray" />
            </span>
          ))}
        </span>
      )}
      {props.darkMode ? (
        <DarkModeIcon className="!text-[38px] xl:!text-[42px] !fill-[#dbdbdb]" />
      ) : (
        <LightModeIcon className="!text-[42px] xl:!text-[46px] !fill-[#f9e686]" />
      )}
    </button>
  );

  return (
    // Clipped sideways: the sun burst's rays reach past the right edge on phones, and Android
    // Chrome zooms the whole page out to fit anything wider than the screen. `clip` (not
    // `hidden`) leaves the drop-down menu free to hang below the bar.
    <header className="navbar relative z-40 overflow-x-clip bg-[#f07364] dark:bg-[#0f193d]">
      {showClouds && (
        <div key={animVersion} className="clouds-container" aria-hidden="true">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="cloud" />
          ))}
        </div>
      )}

      <nav
        aria-label="Main"
        className="relative z-10 mx-auto flex h-[72px] xl:h-20 max-w-6xl items-center justify-between gap-4 px-4 sm:px-6"
      >
        {/* Name and monogram, linking home */}
        <Link
          to="/"
          className="group flex min-w-0 items-center gap-2.5 rounded-lg sm:gap-3 text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          <span
            aria-hidden="true"
            className="flex h-10 w-10 shrink-0 sm:h-11 sm:w-11 xl:h-12 xl:w-12 items-center justify-center rounded-full bg-white text-[15px] sm:text-[16px] xl:text-[18px] font-bold tracking-wide text-[#d63c4b] shadow-sm transition-transform group-hover:scale-105 dark:bg-[#c9a7ff] dark:text-[#0f193d]"
          >
            MO
          </span>
          {/* Below 340px the name would run under the buttons, so only the monogram shows; the
              name stays for screen readers as the link's label. */}
          <span className="sr-only min-[340px]:not-sr-only flex flex-col leading-tight">
            <span className="whitespace-nowrap text-[18px] font-bold tracking-tight sm:text-[21px] xl:text-[24px]">Mohamed Oulal</span>
            <span className={`hidden text-[15px] font-semibold xl:block ${softText}`}>Senior Software Engineer</span>
          </span>
        </Link>

        {/* Desktop links */}
        <div className="hidden items-center gap-4 xl:gap-8 lg:flex">
          {NAV_LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} className={desktopLinkClass}>
              {l.label}
            </NavLink>
          ))}
          <NavLink to={FEATURED.to} className={featuredDesktopClass} title={FEATURED.blurb}>
            {liveDot}
            {FEATURED.label}
          </NavLink>
          <NavLink
            to="/contact"
            className="whitespace-nowrap rounded-full bg-white px-4 xl:px-6 py-2 xl:py-2.5 text-[16px] xl:text-[18px] font-semibold text-[#d63c4b] shadow-sm transition hover:bg-white/90 hover:shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#f07364] dark:bg-[#c9a7ff] dark:text-[#0f193d] dark:hover:bg-[#d8bfff] dark:focus-visible:ring-offset-[#0f193d]"
          >
            Contact me
          </NavLink>
          {themeToggle}
        </div>

        {/* Phone: theme toggle and menu button */}
        <div className="flex items-center lg:hidden">
          {themeToggle}
          <button
            ref={menuButtonRef}
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            className={`menu-toggle flex h-11 w-11 items-center justify-center rounded-full transition-colors hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-white ${menuOpen ? "is-open" : ""}`}
          >
            <span className="burger" aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
          </button>
        </div>
      </nav>

      {/* Phone menu: drops down under the bar, over a dimmed page */}
      <div
        className={`fixed inset-0 top-[72px] z-0 bg-black/40 transition-opacity duration-200 lg:hidden ${menuOpen ? "opacity-100" : "pointer-events-none opacity-0"}`}
        onClick={() => setMenuOpen(false)}
        aria-hidden="true"
      />
      <div
        id="mobile-menu"
        className={`mobile-menu absolute inset-x-0 top-full z-10 rounded-b-3xl bg-[#f07364] px-4 pb-6 pt-2 shadow-xl dark:bg-[#0f193d] lg:hidden ${menuOpen ? "is-open" : ""}`}
        inert={menuOpen ? undefined : ""}
      >
        <div className="mx-auto flex max-w-md flex-col gap-1 border-t border-white/20 pt-3">
          <NavLink
            to={FEATURED.to}
            className={({ isActive }) =>
              "featured-tab mb-2 flex items-center gap-3 rounded-2xl px-4 py-3 text-white " + (isActive ? "is-active" : "")
            }
          >
            {liveDot}
            <span className="flex flex-col leading-tight">
              <span className="text-[19px] font-bold">{FEATURED.label}</span>
              <span className={`text-[14px] font-semibold ${softText}`}>{FEATURED.blurb}</span>
            </span>
            <span aria-hidden="true" className="ml-auto text-[20px] font-bold">&rarr;</span>
          </NavLink>
          {NAV_LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} className={mobileLinkClass}>
              {l.label}
            </NavLink>
          ))}
          <NavLink
            to="/contact"
            className="mt-3 flex items-center justify-center rounded-full bg-white px-5 py-3 text-[19px] font-semibold text-[#d63c4b] shadow-sm transition hover:bg-white/90 dark:bg-[#c9a7ff] dark:text-[#0f193d] dark:hover:bg-[#d8bfff]"
          >
            Contact me
          </NavLink>
        </div>
      </div>
    </header>
  );
}

export default Navbar;
